/**
 * A nota da consulta pela IA sobre o Supabase (0155, 0156).
 *
 * Iniciar, aprovar e descartar são RPCs: as regras (consentimento, uma nota
 * aberta por consulta, só quem gravou aprova) moram no banco. Transcrever e
 * gerar passam pela borda `nota-ia`, que confere a pessoa pela
 * `nota_de_ia_para_gravar` antes de chamar a IA.
 */

import { supabase } from '@/supabase';
import type { NotaDeIa, ParteDaNota, ResumoDaConsulta, SegmentoDaFala, SituacaoDaNota } from './notaDeIa';
import type { PortaDeDados } from './porta';
import type { RespostaDaFicha } from './tipos';

type MetodosDaNota = Pick<
    PortaDeDados,
    | 'notasDeIaDaConsulta' | 'iniciarNotaDeIa' | 'enviarParteDaNotaDeIa' | 'partesDaNotaDeIa'
    | 'gerarNotaDeIa' | 'aprovarNotaDeIa' | 'descartarNotaDeIa' | 'resumosDaIaDoPaciente'
>;

type Linha = Record<string, unknown>;

/* A chave para membros é composta (clínica, autor): `membros!autor_id` o PostgREST recusa (PGRST200). */
const CAMPOS = 'id, atendimento_id, contato_id, modo, situacao, modelo_id, consentimento_em, sugestao, resumo_sugerido, resumo, erro, ficha_id, criado_em, decidida_em, membros!notas_de_ia_clinica_id_autor_id_fkey(nome)';

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error(e.message || 'Esta ação exige a permissão de atendimento.');
    /* A regra já fala com quem está na tela: a frase é do banco. */
    if (e.code === 'P0001' || e.code === '22023') throw new Error(e.message);
    throw new Error(`${mensagem}: ${e.message}`);
}

const texto = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const opcional = (v: unknown) => (v === null || v === undefined || v === '' ? undefined : String(v));

function nomeEmbutido(v: unknown): string | undefined {
    const x = Array.isArray(v) ? v[0] : v;
    return x && typeof x === 'object' ? ((x as { nome?: string }).nome ?? undefined) : undefined;
}

export function paraNota(l: Linha): NotaDeIa {
    const autor = nomeEmbutido(l.membros);
    const sugestao = l.sugestao && typeof l.sugestao === 'object' ? (l.sugestao as Record<string, RespostaDaFicha>) : undefined;
    const campos: Partial<NotaDeIa> = {};
    const modeloId = opcional(l.modelo_id);
    if (modeloId) campos.modeloId = modeloId;
    const consentimentoEm = opcional(l.consentimento_em);
    if (consentimentoEm) campos.consentimentoEm = consentimentoEm;
    if (sugestao) campos.sugestao = sugestao;
    const resumoSugerido = opcional(l.resumo_sugerido);
    if (resumoSugerido) campos.resumoSugerido = resumoSugerido;
    const resumo = opcional(l.resumo);
    if (resumo) campos.resumo = resumo;
    const falha = opcional(l.erro);
    if (falha) campos.erro = falha;
    const fichaId = opcional(l.ficha_id);
    if (fichaId) campos.fichaId = fichaId;
    if (autor) campos.autor = autor;
    const decididaEm = opcional(l.decidida_em);
    if (decididaEm) campos.decididaEm = decididaEm;
    return {
        id: texto(l.id),
        atendimentoId: texto(l.atendimento_id),
        contatoId: texto(l.contato_id),
        modo: l.modo === 'consulta' ? 'consulta' : 'ditado',
        situacao: texto(l.situacao) as SituacaoDaNota,
        criadoEm: texto(l.criado_em),
        ...campos,
    };
}

/** A recusa da borda vem em `{ erro }`; o cliente das funções a esconde no `context`. */
async function invocar(caminho: string, corpo: Linha | FormData, falha: string): Promise<Linha> {
    const { data, error } = await cliente().functions.invoke(caminho, { body: corpo, method: 'POST' });
    if (error) {
        const contexto = (error as { context?: unknown }).context;
        if (contexto instanceof Response) {
            let mensagem = falha;
            try {
                const lido = (await contexto.clone().json()) as Linha;
                if (typeof lido.erro === 'string' && lido.erro) mensagem = lido.erro;
            } catch {
                /* corpo que não é JSON: fica a frase genérica */
            }
            throw new Error(mensagem);
        }
        throw new Error(error instanceof Error ? error.message : falha);
    }
    if (typeof data !== 'object' || data === null) throw new Error(falha);
    return data as Linha;
}

function extensao(tipo: string): string {
    if (tipo.includes('mp4')) return 'm4a';
    if (tipo.includes('ogg')) return 'ogg';
    if (tipo.includes('wav')) return 'wav';
    return 'webm';
}

export const notaDeIaNoSupabase: MetodosDaNota = {
    async notasDeIaDaConsulta(atendimentoId: string): Promise<NotaDeIa[]> {
        /* `at-…` é consulta que ainda não virou atendimento: não tem nota. */
        if (atendimentoId.startsWith('at-')) return [];
        const { data, error } = await cliente()
            .from('notas_de_ia')
            .select(CAMPOS)
            .eq('atendimento_id', atendimentoId)
            .order('criado_em', { ascending: false });
        erro('Falha ao ler as notas da IA', error);
        return ((data ?? []) as unknown as Linha[]).map(paraNota);
    },

    async iniciarNotaDeIa(atendimentoId, modo, opcoes): Promise<NotaDeIa> {
        const { data, error } = await cliente().rpc('iniciar_nota_de_ia', {
            p_atendimento: atendimentoId,
            p_modo: modo,
            p_consentimento: opcoes.consentimento,
            p_modelo: opcoes.modeloId ?? null,
        });
        erro('Falha ao começar a gravação', error);
        const lida = await cliente().from('notas_de_ia').select(CAMPOS).eq('id', data as string).single();
        erro('A gravação começou, mas não foi possível relê-la', lida.error);
        return paraNota(lida.data as unknown as Linha);
    },

    async enviarParteDaNotaDeIa(notaId, parte): Promise<void> {
        if (parte.audio.size === 0) throw new Error('A parte do áudio ficou vazia.');
        const formulario = new FormData();
        formulario.append('notaId', notaId);
        formulario.append('ordem', String(parte.ordem));
        formulario.append('segundos', String(parte.segundos));
        formulario.append('audio', parte.audio, `parte-${parte.ordem}.${extensao(parte.audio.type)}`);
        await invocar('nota-ia/parte', formulario, 'Não foi possível transcrever esta parte da gravação.');
    },

    async partesDaNotaDeIa(notaId: string): Promise<ParteDaNota[]> {
        const { data, error } = await cliente()
            .from('partes_da_nota_de_ia')
            .select('ordem, segundos, texto, segmentos')
            .eq('nota_id', notaId)
            .order('ordem');
        erro('Falha ao ler a transcrição', error);
        return ((data ?? []) as unknown as Linha[]).map((p) => ({
            ordem: Number(p.ordem),
            segundos: Number(p.segundos),
            texto: texto(p.texto),
            ...(Array.isArray(p.segmentos) ? { segmentos: p.segmentos as SegmentoDaFala[] } : {}),
        }));
    },

    async gerarNotaDeIa(notaId: string): Promise<NotaDeIa> {
        const r = await invocar('nota-ia/gerar', { notaId }, 'Não foi possível escrever a nota.');
        return paraNota(r.nota as Linha);
    },

    async aprovarNotaDeIa(notaId, respostas, resumo): Promise<string> {
        const { data, error } = await cliente().rpc('aprovar_nota_de_ia', { p_nota: notaId, p_respostas: respostas, p_resumo: resumo });
        erro('Falha ao aprovar a nota', error);
        return texto(data);
    },

    async descartarNotaDeIa(notaId: string): Promise<void> {
        const { error } = await cliente().rpc('descartar_nota_de_ia', { p_nota: notaId });
        erro('Falha ao descartar a nota', error);
    },

    async resumosDaIaDoPaciente(contatoId: string): Promise<ResumoDaConsulta[]> {
        const { data, error } = await cliente().rpc('resumos_da_ia_do_paciente', { p_contato: contatoId });
        erro('Falha ao ler os resumos da IA', error);
        return ((data ?? []) as unknown as Linha[]).map((r) => {
            const autor = opcional(r.autor);
            const resumo = opcional(r.resumo);
            return {
                notaId: texto(r.nota_id),
                atendimentoId: texto(r.atendimento_id),
                modo: r.modo === 'consulta' ? 'consulta' : 'ditado',
                aprovadoEm: texto(r.decidida_em),
                visivel: r.visivel === true,
                ...(autor ? { autor } : {}),
                ...(resumo ? { resumo } : {}),
            };
        });
    },
};
